// Часы: показ и точное сложение. Часы приходят с сервера СТРОКОЙ («63.5»), потому что
// float64 теряет десятую долю на сумме по постам (ПРАВИЛА.md, раздел 3).
//
// Арифметика здесь ровно одна и ровно та же, что в движке сервера
// (server/app/domain/norms.py, norm_hours_from_posts): сумма часов в сутки по активным
// постам. Норматив за месяц считает сервер и отдаёт готовым — его мы НЕ пересчитываем,
// показываем как есть.
//
// Считаем в целых долях через BigInt, а не через parseFloat: 12.1 + 24.2 в плавающей
// точке даёт 36.300000000000004, и в подписи под списком постов это выглядит поломкой.

/** Неразрывный пробел между разрядами: перенос строки внутри числа разрывает его глазу. */
const NBSP = '\u00a0';
/** Типографский минус, а не дефис: дефис теряется рядом с цифрами. */
const MINUS = '\u2212';

const DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d*))?$/;

interface ParsedDecimal {
  /** Значение в долях: «63.5» при scale 1 — это 635. */
  units: bigint;
  /** Сколько знаков после запятой. */
  scale: number;
}

function parseHours(value: string): ParsedDecimal {
  const match = DECIMAL_PATTERN.exec(value.trim());
  if (match === null) {
    throw new Error(`часы передаются десятичной строкой, получено «${value}»`);
  }
  const sign = match[1] ?? '';
  const whole = match[2] ?? '0';
  const fraction = match[3] ?? '';
  const units = BigInt(whole + fraction);
  return { units: sign === '-' ? -units : units, scale: fraction.length };
}

function toScale(parsed: ParsedDecimal, scale: number): bigint {
  return parsed.units * 10n ** BigInt(scale - parsed.scale);
}

function render(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale === 0 ? '' : digits.slice(digits.length - scale);
  const body = fraction === '' ? whole : `${whole}.${fraction}`;
  return negative ? `-${body}` : body;
}

/** Сумма часов десятичными строками. Результат — тоже строка, без плавающей точки. */
export function sumHours(values: readonly string[]): string {
  const parsed = values.map(parseHours);
  const scale = parsed.reduce((max, item) => Math.max(max, item.scale), 0);
  let total = 0n;
  for (const item of parsed) {
    total += toScale(item, scale);
  }
  return render(total, scale);
}

/** Разность часов: «сколько ручной норматив отличается от расчётного». Знак сохраняется. */
export function diffHours(left: string, right: string): string {
  const a = parseHours(left);
  const b = parseHours(right);
  const scale = Math.max(a.scale, b.scale);
  return render(toScale(a, scale) - toScale(b, scale), scale);
}

/** Совпадают ли две записи часов по значению: «558» и «558.0» — одно и то же число. */
export function sameHours(left: string, right: string): boolean {
  return parseHours(diffHours(left, right)).units === 0n;
}

/** Первое число часов строго больше второго: «400» больше «372.0», «372» не больше «372.0». */
export function moreHours(left: string, right: string): boolean {
  return parseHours(diffHours(left, right)).units > 0n;
}

/**
 * Показ часов: «558», «63,5», «1 800». Незначащие нули дробной части срезаются —
 * «558.00» человек читает как «558».
 *
 * null, undefined и пустая строка — это «данных нет», прочерк (правило 7). Ноль — это
 * результат расчёта, и он показывается нулём: путать их нельзя.
 */
export function formatHours(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const parsed = parseHours(value);
  const negative = parsed.units < 0n;
  const digits = (negative ? -parsed.units : parsed.units)
    .toString()
    .padStart(parsed.scale + 1, '0');
  const whole = digits.slice(0, digits.length - parsed.scale);
  const fraction = digits.slice(digits.length - parsed.scale).replace(/0+$/, '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const body = fraction === '' ? grouped : `${grouped},${fraction}`;
  return negative ? `${MINUS}${body}` : body;
}

/** «час / часа / часов» по числу: подпись под числом обязана согласовываться с ним. */
export function hoursWord(value: string): string {
  const parsed = parseHours(value);
  if (parsed.scale > 0 && parsed.units % 10n ** BigInt(parsed.scale) !== 0n) {
    // Дробное число часов: «63,5 часа».
    return 'часа';
  }
  const whole = Number(
    (parsed.units < 0n ? -parsed.units : parsed.units) / 10n ** BigInt(parsed.scale),
  );
  const lastTwo = whole % 100;
  const last = whole % 10;
  if (lastTwo >= 11 && lastTwo <= 14) {
    return 'часов';
  }
  if (last === 1) {
    return 'час';
  }
  if (last >= 2 && last <= 4) {
    return 'часа';
  }
  return 'часов';
}
